import classNames from "classnames";
import { Immutable } from "immer";
import React, { type JSX } from "react";
import { FilterDistanceSelect } from "tsi-common-react/src/apps/retail/FilterDistanceSelect/index";
import {
    MarkerMap,
    type MarkerMapHandle,
} from "tsi-common-react/src/apps/retail/MarkerMap/index";
import { formatDistanceWithSuffix } from "tsi-common-react/src/apps/retail/helpers";
import { Link } from "tsi-common-react/src/components/Link/index";
import { LocationInput } from "tsi-common-react/src/components/LocationInput/index";
import { ILocation, RetailStore } from "tsi-common-react/src/models/location";
import {
    SyncStoreID,
    isoPostcode,
    isoStateA2,
} from "tsi-common-react/src/models/nominals";
import { formatPhoneNumber } from "tsi-common-react/src/utils/format";
import { useCurrentBreakpoint } from "tsi-common-react/src/utils/hooks";
import { useStaticURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import iconRlPin from "../../../assets/img/icons/icon-rl-pin.png";

const PAGE_SIZE = 15;

interface ILocationProps {
    storeNumber: number;
    store: Immutable<RetailStore>;
    isFocused: boolean;
    onSelect: (storeID: SyncStoreID) => void;
}

interface IContainerProps {
    stores: Immutable<RetailStore[]>;
    focusedStoreID: SyncStoreID | null;
    location: ILocation | null;
    onBeforeInfoWindowOpen: (
        info: google.maps.InfoWindow,
        store: Immutable<RetailStore>,
    ) => void;
    onInfoWindowClose: () => void;
}

const RetailLocation = (props: ILocationProps) => {
    const staticURL = useStaticURL();
    // encode destination address for Directions link
    const destinationAddress = encodeURI(
        props.store.address +
            " " +
            props.store.address2 +
            " " +
            props.store.city +
            "," +
            isoStateA2.unwrap(props.store.state) +
            " " +
            isoPostcode.unwrap(props.store.postal),
    );
    const articleClasses = classNames({
        "list-focus": props.isFocused,
    });
    const directionsURL = `https://www.google.com/maps/dir/?api=1&destination=${destinationAddress}`;
    let websiteLinkElement: JSX.Element | null = null;

    if (props.store.web_sealy) {
        websiteLinkElement = (
            <div className="loc-website">
                <Link
                    href={props.store.web_sealy}
                    target="_blank"
                    className="loc-website"
                    rel="nofollow noreferrer"
                >
                    {/* TODO(i18n): Wrap user-facing string in t tagged template: {t`Website`} */}
                    Website
                    {/* TODO(i18n): Translate alt attribute with t tagged template: alt={t`Website`} */}
                    <img
                        src={`${staticURL}img/retail-locator/open-link-icon-blue.png`}
                        alt="Website"
                        loading="lazy"
                        decoding="async"
                    />
                </Link>
            </div>
        );
    }

    return (
        <li>
            <button
                className={articleClasses}
                onClick={() => {
                    props.onSelect(props.store.external_id);
                }}
                aria-current={props.isFocused}
            >
                <div className="list-label">{props.storeNumber}</div>
                <div className="list-details">
                    <div className="list-content">
                        <h4 className="loc-name">{props.store.name}</h4>
                        <div className="loc-addr">{props.store.address}</div>
                        <div className="loc-addr2">{props.store.address2}</div>
                        <div className="loc-addr3">
                            <span className="loc-city">{props.store.city}</span>
                            ,&nbsp;
                            <span className="loc-state">
                                {isoStateA2.unwrap(props.store.state)}
                            </span>
                            &nbsp;
                            <span className="loc-postal">
                                {isoPostcode.unwrap(props.store.postal)}
                            </span>
                        </div>
                        {props.store.phone && (
                            <div className="loc-phone">
                                <a href={`tel:+${props.store.phone}`}>
                                    {formatPhoneNumber(props.store.phone)}
                                </a>
                            </div>
                        )}
                        <div className="loc-dist">
                            {props.store.distance &&
                                formatDistanceWithSuffix(
                                    props.store.distance,
                                    2,
                                )}
                        </div>
                        <div className="loc-directions">
                            <a
                                href={directionsURL}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {t`Directions`}
                            </a>
                        </div>
                        {websiteLinkElement}
                    </div>
                </div>
            </button>
        </li>
    );
};

const getStoreNumber = (
    stores: Immutable<RetailStore[]>,
    store: Immutable<RetailStore>,
): number => {
    return stores.indexOf(store) + 1;
};

export const RetailLocatorBlock = (props: IContainerProps): React.ReactNode => {
    const mapElem = React.useRef<MarkerMapHandle>(null);
    const listRef = React.useRef<HTMLUListElement>(null);
    const focusIndexRef = React.useRef<number | null>(null);
    const storesRef = React.useRef(props.stores);
    storesRef.current = props.stores;
    const isAllSmall = useCurrentBreakpoint().belowMobile;
    const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);

    React.useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [props.stores]);

    React.useEffect(() => {
        const idx = focusIndexRef.current;
        if (idx === null) {
            return;
        }
        focusIndexRef.current = null;
        listRef.current
            ?.querySelectorAll<HTMLButtonElement>(":scope > li > button")
            [idx]?.focus();
    }, [visibleCount]);

    const locationInputID = "retail-locator-location-input";
    const distanceInputID = "retail-locator-distance-input";

    const visibleStores = isAllSmall
        ? props.stores.slice(0, visibleCount)
        : props.stores;
    const showLoadMore = isAllSmall && visibleCount < props.stores.length;

    return (
        <>
            <div className="retailers__hero">
                <div className="container bh-sl-container">
                    <div className="retailers__form">
                        <h3 className="retailers__headline">
                            {t`Find a Retailer`}
                        </h3>
                        <div className="retailers__form-container bh-sl-form-container">
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                }}
                            >
                                <div className="retailers__form-input form-input">
                                    <label
                                        className="retailers__form-label retailers__form-label--address legal"
                                        htmlFor={locationInputID}
                                    >
                                        <LocationInput
                                            inputFormat="short"
                                            inputProps={{
                                                id: locationInputID,
                                                className:
                                                    "retailers__form-address",
                                                name: "bh-sl-address",
                                                placeholder: t`Zip or City, State`,
                                            }}
                                        />
                                    </label>
                                    <label
                                        className="retailers__form-label retailers__form-label--distance legal"
                                        htmlFor={distanceInputID}
                                    >
                                        <FilterDistanceSelect
                                            selectProps={{
                                                id: distanceInputID,
                                                className:
                                                    "retailers__form-label-arrow",
                                                name: "bh-sl-maxdistance",
                                                title: t`Retailer Distance`,
                                            }}
                                        />
                                    </label>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <div
                id="bh-sl-map-container"
                className="container bh-sl-map-container"
            >
                <MarkerMap
                    ref={mapElem}
                    stores={props.stores}
                    markerOptions={(store: Immutable<RetailStore>) => {
                        return {
                            icon: iconRlPin,
                            label: {
                                text: `${getStoreNumber(props.stores, store)}`,
                                color: "#000",
                                className: "marker-label",
                                fontWeight: "light",
                            },
                        };
                    }}
                    elemProps={{
                        id: "bh-sl-map",
                        className: "bh-sl-map",
                    }}
                    onBeforeInfoWindowOpen={(info, store) => {
                        const idx = storesRef.current.findIndex(
                            (s) => s.external_id === store.external_id,
                        );
                        if (idx >= 0) {
                            setVisibleCount((c) =>
                                Math.max(
                                    c,
                                    Math.ceil((idx + 1) / PAGE_SIZE) *
                                        PAGE_SIZE,
                                ),
                            );
                        }
                        props.onBeforeInfoWindowOpen(info, store);
                    }}
                    onInfoWindowClose={props.onInfoWindowClose}
                />
                <div className="bh-sl-loc-list">
                    <ul className="list" ref={listRef}>
                        {visibleStores.map((store, i) => (
                            <RetailLocation
                                {...props}
                                key={`${store.external_id}`}
                                storeNumber={i + 1}
                                store={store}
                                isFocused={
                                    props.focusedStoreID === store.external_id
                                }
                                onSelect={(storeID) => {
                                    mapElem.current?.onActivateMarker(storeID);
                                }}
                            />
                        ))}
                        {props.stores.length <= 0 && (
                            <li>
                                <div className="bh-sl-noresults-title">
                                    {t`No results`}
                                </div>
                                <br />
                                <div className="bh-sl-noresults-desc">
                                    {t`No locations were found with the given criteria. Please modify your selections or input.`}
                                </div>
                            </li>
                        )}
                    </ul>
                    {showLoadMore && (
                        <button
                            type="button"
                            className="button retailers__load-more"
                            onClick={() => {
                                focusIndexRef.current = visibleCount;
                                setVisibleCount((count) => count + PAGE_SIZE);
                            }}
                        >
                            {t`Load more retailers`}
                        </button>
                    )}
                </div>
            </div>
        </>
    );
};
